/**
 * Issue #267/#357 — 「系统设置 → 语音识别API → 实时语音转写模型」纯逻辑 + 文案。
 *
 * 后端已支持 `PATCH /admin/asr/settings` 的 `scenarios.sopRealtime / qcRealtime`（两条独立实时链，
 * 均独立于文件 ASR 的 `primary/fallbackOrder`）。本模块把「当前选中解析 / 保存请求体构造 / 选项是否
 * 可选」抽成纯函数，由 AsrProviderSettingsCard 薄包装并按场景复用，关键不变式（保存只写目标实时
 * scenario、恢复默认写 { scenarios: { [scenario]: null } }、绝不污染 primary/fallbackOrder）由单测锁定。
 *
 * 默认继承链（部署默认，首位为讯飞实时语音转写大模型）：
 *   xfyun_realtime_llm → xfyun_realtime_standard → aliyun_bailian_realtime → xfyun_iat
 */

/** Issue #357 — 两个独立实时场景：SOP 培训实时识别 / 质检中心实时识别。 */
export type AsrRealtimeScenario = 'sopRealtime' | 'qcRealtime';

/** 实时模型选择的「继承部署默认」哨兵（与 file ASR 的 INHERIT 同义，独立常量避免耦合）。 */
export const REALTIME_INHERIT = '__inherit__';

/** Issue #267 默认实时继承链（仅用于 UI 文案 / 单测断言；运行时以后端 defaultRealtimeOrder 为准）。 */
export const DEFAULT_REALTIME_INHERIT_CHAIN: readonly string[] = [
  'xfyun_realtime_llm',
  'xfyun_realtime_standard',
  'aliyun_bailian_realtime',
  'xfyun_iat',
];

/** 实时 provider 中文短标签（下拉展示用；未知 id 回退原 id）。 */
export const REALTIME_PROVIDER_SHORT_LABELS: Record<string, string> = {
  xfyun_realtime_llm: '科大讯飞实时语音转写大模型',
  xfyun_realtime_standard: '科大讯飞实时语音转写标准版',
  aliyun_bailian_realtime: '阿里云百炼实时语音识别',
  xfyun_iat: '科大讯飞实时听写兜底',
};

export interface AsrRealtimeScenarioPatch {
  scenarios: Partial<Record<AsrRealtimeScenario, string[] | null>>;
}

/**
 * 解析当前实时主用模型选择：override 非空数组 → 取首位为主用；null/空 → 继承部署默认。
 */
export function resolveRealtimeSelection(
  qcRealtimeOverride: readonly string[] | null | undefined,
): string {
  if (Array.isArray(qcRealtimeOverride) && qcRealtimeOverride.length > 0) {
    return qcRealtimeOverride[0];
  }
  return REALTIME_INHERIT;
}

/**
 * Issue #357 — 构造保存实时主用模型的 PATCH 请求体。**只**写目标实时 `scenarios.<scenario>`
 * （sopRealtime / qcRealtime），绝不带 `primary` / `fallbackOrder`（那是文件 ASR 概念，污染会改写
 * 批量链）：
 *   - 选具体模型 X：<scenario> = [X, ...其余实时 provider]（X 置顶为主用，其余作兜底链）；
 *   - 继承部署默认：<scenario> = null（回退后端 defaultRealtimeOrder / 环境变量）。
 *
 * `scenario` 默认 `qcRealtime` 以兼容旧调用点。
 */
export function buildRealtimeScenarioPatch(
  selectedId: string,
  realtimeProviderIds: readonly string[],
  scenario: AsrRealtimeScenario = 'qcRealtime',
): AsrRealtimeScenarioPatch {
  if (selectedId === REALTIME_INHERIT) {
    return { scenarios: { [scenario]: null } };
  }
  const rest = realtimeProviderIds.filter((id) => id !== selectedId);
  return { scenarios: { [scenario]: [selectedId, ...rest] } };
}

/**
 * 某实时 provider 是否可被选为「主用」：streamable=false（如阿里云百炼实时，流式接入待后续）
 * 不可选作主用——下拉里禁用该项，明确「未完全接入」。
 */
export function isRealtimeOptionSelectable(provider: {
  streamable?: boolean;
}): boolean {
  return provider.streamable !== false;
}

/** 实时 provider 短标签解析（未知 id 回退原 id）。 */
export function realtimeProviderShortLabel(id: string): string {
  return REALTIME_PROVIDER_SHORT_LABELS[id] ?? id;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Issue #363（返修 P0-2）— **完整 1-2-3 兜底顺序**编辑器纯逻辑。
 *
 * 旧实现只让管理员选一个「主用」模型、其余按 server/provider-list 固定顺序拼接，无法配置完整
 * 顺序。这里把「有序 + 可增删 provider」的编辑抽成纯函数，由 AsrProviderSettingsCard 薄包装：
 *   - resolveRealtimeOrderEditor：从后台 override 还原编辑器状态（override 顺序在前 included，
 *     其余 provider 在后 not-included）；
 *   - moveRealtimeRow / toggleRealtimeRow：上下移动 / 勾选参与兜底；
 *   - includedRealtimeOrder：取当前 included 行的**精确顺序**；
 *   - buildRealtimeOrderPatch：只写 scenarios.<scenario> 的**精确有序数组**（绝不动 primary/fallbackOrder）。
 * ──────────────────────────────────────────────────────────────────────────── */

export interface RealtimeOrderRow {
  id: string;
  /** 是否参与该场景的实时兜底链（未勾选的 provider 不写入保存数组）。 */
  included: boolean;
}

export interface RealtimeOrderEditorState {
  /** true = 继承部署默认（保存写 null）；false = 使用下方自定义有序链。 */
  inherit: boolean;
  rows: RealtimeOrderRow[];
}

/**
 * 从后台 `scenarios.<scenario>` override 还原有序编辑器状态：
 *   - override 非空数组 → inherit=false，override 里的已知 id 按其顺序在前（included），
 *     其余实时 provider 追加在后（not-included）；
 *   - null / 空 / undefined → inherit=true，全部 provider 按部署默认顺序、均 included（作为自定义起点）。
 */
export function resolveRealtimeOrderEditor(
  override: readonly string[] | null | undefined,
  allRealtimeIds: readonly string[],
): RealtimeOrderEditorState {
  const known = allRealtimeIds.filter((id, i) => allRealtimeIds.indexOf(id) === i);
  if (Array.isArray(override) && override.length > 0) {
    const inOverride = override.filter((id) => known.includes(id));
    const seen = new Set(inOverride);
    const rest = known.filter((id) => !seen.has(id));
    return {
      inherit: false,
      rows: [
        ...inOverride.map((id) => ({ id, included: true })),
        ...rest.map((id) => ({ id, included: false })),
      ],
    };
  }
  return { inherit: true, rows: known.map((id) => ({ id, included: true })) };
}

/** 上（dir=-1）/ 下（dir=+1）移动一行；越界不动。返回新数组（不可变）。 */
export function moveRealtimeRow(
  rows: readonly RealtimeOrderRow[],
  index: number,
  dir: -1 | 1,
): RealtimeOrderRow[] {
  const next = rows.map((r) => ({ ...r }));
  const j = index + dir;
  if (index < 0 || index >= next.length || j < 0 || j >= next.length) return next;
  const tmp = next[index];
  next[index] = next[j];
  next[j] = tmp;
  return next;
}

/** 切换某行是否参与兜底链。返回新数组（不可变）。 */
export function toggleRealtimeRow(
  rows: readonly RealtimeOrderRow[],
  index: number,
): RealtimeOrderRow[] {
  return rows.map((r, i) => (i === index ? { ...r, included: !r.included } : { ...r }));
}

/** 取当前 included 行的**精确顺序** id 数组。 */
export function includedRealtimeOrder(rows: readonly RealtimeOrderRow[]): string[] {
  return rows.filter((r) => r.included).map((r) => r.id);
}

/**
 * Issue #363（返修 P0-2）— 构造保存**完整有序兜底链**的 PATCH。**只**写目标实时 scenarios.<scenario>
 * （sopRealtime / qcRealtime），绝不带 primary / fallbackOrder。
 *   - inherit（或 included 为空）→ scenarios.<scenario> = null（回退后端 defaultRealtimeOrder / env）；
 *   - 否则 → scenarios.<scenario> = **精确** included 有序数组（如
 *     `['aliyun_bailian_realtime','xfyun_realtime_standard','xfyun_realtime_llm']` 原样保存）。
 */
export function buildRealtimeOrderPatch(
  editor: RealtimeOrderEditorState,
  scenario: AsrRealtimeScenario = 'qcRealtime',
): AsrRealtimeScenarioPatch {
  const included = editor.inherit ? [] : includedRealtimeOrder(editor.rows);
  if (included.length === 0) {
    return { scenarios: { [scenario]: null } };
  }
  return { scenarios: { [scenario]: included } };
}

/** 两个编辑器状态是否等价（用于 dirty 判定）。 */
export function realtimeEditorsEqual(
  a: RealtimeOrderEditorState,
  b: RealtimeOrderEditorState,
): boolean {
  if (a.inherit !== b.inherit) return false;
  if (a.rows.length !== b.rows.length) return false;
  return a.rows.every(
    (row, i) => row.id === b.rows[i].id && row.included === b.rows[i].included,
  );
}
